import { AlertCircleIcon, FolderIcon, FolderOpenIcon, SearchIcon } from 'lucide-react';
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useMS } from 'src/components/files/onedrive/context/MSContext';
import { ScrollArea, ScrollBar } from 'src/components/ui/scroll-area';
import { Tab, TabItem } from 'src/components/ui/tab'
import { CONFIG } from 'src/config';
import { cleanupTrash, createFolderInOneDrive, getDriveItems, getOneDriveFiles } from 'src/graph';
import { useAppSelector } from 'src/hooks/redux';
import { getWindevLang } from 'src/lib/lang';
import { RootState } from 'src/store';
import DocumentsTable from './documents-table';
import { Input } from 'src/components/ui/input';
import ProfilePicture from './profile-picture';
import { hasDoctypeRight } from 'src/lib/filter';

const HomePage = () => {
  const { i18n } = useTranslation()
  const doctypes:DoctypeObject[] = useAppSelector((state:RootState)=>state.doctypes)
  const docgroups:DocgroupObject[] = useAppSelector((state:RootState)=>state.docgroups)

  const { accessToken, setfitdocfolder, doctypesfolder, setdoctypesfolder, setdoctypescollection, doctypescollection } = useMS()

  const [search_doctype, setsearch_doctype] = useState('');
  const [selected_tab, setselected_tab] = useState('onedrive');
  const [selected_doctype, setselected_doctype] = useState<string | undefined>(doctypes ? doctypes[0].doctype : undefined);
  

  async function completeDoctypesFolder() {
    const odf = await getOneDriveFiles(accessToken);
    const fitdocFolder = odf.value.find((f:any) => f.name === CONFIG.onedrive.fitdoc_foldername)
    const parentId = odf.value[0].parentReference.id

    if(!fitdocFolder){ 
      const folder = await createFolderInOneDrive(accessToken, parentId, CONFIG.onedrive.fitdoc_foldername)
      const dtf = await createFolderInOneDrive(accessToken, folder.id, CONFIG.onedrive.fitdoc_doctypes_foldername)
      setfitdocfolder(folder)
      setdoctypesfolder(dtf)
      return
    }

    

    const fdItems = await getDriveItems(accessToken, fitdocFolder.id)
    if(fdItems.value){
        const df = fdItems.value.find((f:any)=>f.name === CONFIG.onedrive.fitdoc_doctypes_foldername)
        if(!df){
            const dtf = await createFolderInOneDrive(accessToken, fitdocFolder.id, CONFIG.onedrive.fitdoc_doctypes_foldername)
            setdoctypesfolder(dtf)
        } else { setdoctypesfolder(df) }
    }

    setfitdocfolder(fitdocFolder)

    cleanupTrash(accessToken)
  }

  async function completeDoctypesSubFolders() {

    let collection:{doctype:string, [key:string]:any}[] = []

    const doctypesFolderItems = await getDriveItems(accessToken, doctypesfolder.id)

    const promises = doctypes.map(async (doctype) => {
      if(!hasDoctypeRight(doctype.doctype, doctypes, 'create')){ return; } 

      // find folder with name doctype in doctypesfolder
      const doctypeFolder = doctypesFolderItems.value.find((f:any) => f.name === `${doctypeToName(doctype.doctype)}`)
    
      // if doctype folder not found => create folder
      // else add to collection
      if(!doctypeFolder){
        const createdDoctypeFolder = await createFolderInOneDrive(accessToken, doctypesfolder.id, `${doctypeToName(doctype.doctype)}`)
        collection.push({doctype:doctype.doctype, ...createdDoctypeFolder})
        return;
      } 
      else {
        collection.push({doctype:doctype.doctype, ...doctypeFolder})
        return;
      }
    });

    await Promise.all(promises)

    setdoctypescollection(collection)
  }

  useEffect(()=>{
    if(doctypesfolder && Array.isArray(doctypes)){ completeDoctypesSubFolders() }
  }, [doctypesfolder, doctypes])

  useEffect(()=>{
    if(accessToken){
      completeDoctypesFolder()
    }
  }, [accessToken])

  function doctypeToName (doctype:string) {
    if(!Array.isArray(doctypes)) return doctype;

    const d = doctypes.find(i=>i.doctype === doctype);
    if(!d) return doctype;

    return d.docoms.find(i=>i.nationid === getWindevLang(i18n.language))?.oms || doctype
  }

  function sortDoctypesByDocgroup (doctypes:DoctypeObject[]) {
    let tmp = [...doctypes]

    if(!Array.isArray(docgroups) || !docgroups){ return tmp }

    // Create a map for document type order based on their position in docgroups
    const orderMap:any = {};

    // Iterate over docgroups to fill the orderMap with proper ordering
    docgroups.forEach((group, groupIndex) => {
      group.doctypes.forEach((doc, docIndex) => {
        orderMap[doc.doctype] = { groupIndex, docIndex };
      });
    });

    // Sort the doctypes array based on the orderMap
    tmp.sort((a, b) => {
      const orderA = orderMap[a.doctype];
      const orderB = orderMap[b.doctype];

      // If a doctype is not found in the orderMap, place it at the end (using Infinity)
      if (!orderA) return 1;
      if (!orderB) return -1;

      // Compare by groupIndex first, then by docIndex within the group
      if (orderA.groupIndex === orderB.groupIndex) {
        return orderA.docIndex - orderB.docIndex;
      } else {
        return orderA.groupIndex - orderB.groupIndex;
      }
    });

    return tmp
  }

  return (
    <div className='flex flex-col h-dvh'>
      <div className='bg-white border-b p-4 flex items-center justify-between'>
        <Tab value={selected_tab} onValueChange={(v)=>{setselected_tab(v)}}>
          <TabItem value='onedrive'>OneDrive</TabItem>
        </Tab>
        <ProfilePicture />
      </div>
      <div className='border-b flex flex-col overflow-hidden'>
          <div className='px-4 pt-4'><Input value={search_doctype} onChange={({currentTarget})=>{setsearch_doctype(currentTarget.value)}} startEnhancer={<SearchIcon className='w-4 h-4 text-neutral-400'/>} placeholder='Documenttype zoeken...' className='w-fit'/></div>
          <ScrollArea className='overflow-y-hidden '>
            <div className='flex items-center gap-4 p-4'>
              {doctypes && 
              sortDoctypesByDocgroup(doctypes)
              .filter(d => doctypeToName(d.doctype).toUpperCase().includes(search_doctype.toUpperCase()))
              .map((doctype) => (
                <div onClick={()=>{ setselected_doctype(doctype.doctype) }} className={`cursor-pointer border rounded-lg p-4 bg-white flex flex-col hover:scale-105 hover:shadow-md duration-200 ${selected_doctype === doctype.doctype && 'border-primary'}`} key={`ld-${doctype.doctype}`}>
                  <div className='flex items-center gap-2'>
                    <div className='w-10 min-w-10 h-10 border flex items-center justify-center rounded-md'>
                      {selected_doctype === doctype.doctype 
                      ? <FolderOpenIcon className='w-4 h-4 text-primary'/>
                      : <FolderIcon className='w-4 h-4 text-primary'/>
                      }
                    </div>
                    <div className='flex flex-col'>
                      <p className='whitespace-nowrap text-sm font-medium'>{doctypeToName(doctype.doctype)}</p>
                      <p className='whitespace-nowrap text-xs text-neutral-600'>{Array.isArray(doctypescollection) ? (doctypescollection.find((i:any) => i.doctype === doctype.doctype)?.folder?.childCount || 0) : 0} items</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          <ScrollBar orientation='horizontal'/>
          </ScrollArea>
          
        <div className='px-4 pb-4'>
          <div className='bg-white rounded-lg p-2 border flex items-center gap-2'>
            <AlertCircleIcon className='w-4 h-4 text-red-600'/>
            <p className='text-sm text-neutral-500'><span className='font-medium text-red-600'>Waarschuwing</span> - deze mappen zijn een koppeling tussen OneDrive en FITdoc. Gelieve dit niet te wijzigen van structuur of naam.</p>
          </div>
        </div>

      </div>
      <div className='flex-1 overflow-auto bg-white p-4'>
        <div className='flex flex-col gap-4 w-full h-full'>
          <div>
            <p className='text-lg font-semibold'>Overzicht opslag</p>
            <p className='text-sm text-neutral-600'>Het overzicht van uw <span className='font-medium'>{selected_doctype && doctypeToName(selected_doctype)}</span> documenten</p>
          </div>

          {(doctypescollection && doctypescollection.find((i:any) => i.doctype === selected_doctype)) && <DocumentsTable refetchDoctypesCollection={()=>{completeDoctypesSubFolders()}} doctypeCollection={doctypescollection.find((i:any) => i.doctype === selected_doctype)}/>}

        </div>
      </div>
    </div>
  )
}

export default HomePage